import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import '../../../../src/AssetStatusForm.scss'
import './table-sort-icon.scss';

type SetPageFunction = React.Dispatch<React.SetStateAction<number>>;

interface TableProps {
    data: any[];
    columns: any[];
    defaultSortColumn: string;
    defaultSortOrder: string;
    columnHeaders: any[];
    pagniation?: boolean;
    page: number;
    setPage: SetPageFunction;
    searchValue: string;
    addedRows: any[];
    handleAddRow: () => void;
    setAddedRows: React.Dispatch<React.SetStateAction<any[]>>;
    setFilterData: React.Dispatch<React.SetStateAction<any[]>>;
    editedComments: Record<string, { comment: string; change: string }>;
    setEditedComments: React.Dispatch<React.SetStateAction<Record<string, { comment: string; change: string }>>>;
    isToggle?: boolean;
    showEventDataMessage: boolean;
}

export const EventTable: React.FC<TableProps> = ({ data, columns, defaultSortColumn, defaultSortOrder, columnHeaders, pagniation, page, setPage, searchValue, addedRows, setAddedRows, setFilterData, editedComments, setEditedComments, isToggle, showEventDataMessage }) => {

    const [sortColumn, setSortColumn] = useState(defaultSortColumn);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [showDropdown, setShowDropdown] = useState(false);
    // const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageSelected, setPageSelected] = useState<string>("10")
    const [currentPage, setCurrentPage] = useState(1);
    // const [editedComments, setEditedComments] = useState({});
    const [editingRowIndex, setEditingRowIndex] = useState([]);
    // const [addedRows, setAddedRows] = useState([]);
    const nonSortableColumns = new Set(['closeDuration', 'arrivalVelocity', 'salesDuration', 'projectedGas']);
    // Define dropdown options
    const typeOptions = [
        "Communication error",
        "User SP change",
        "System SP change",
        "Device alarm",
        "User comment"
    ];



    useEffect(() => {
        setCurrentPage(page)
    }, [page])

    useEffect(() => {
        if (Object.keys(editedComments).length === 0) {
            setEditingRowIndex([])
        }
    }, [editedComments])

    const handlePageNumberChange = (pageNumber: number) => {
        setPage(pageNumber)
        setCurrentPage(pageNumber);
    }

    const handlePageSelectedClick = (event: any) => {
        const selectedValue: string = event.target.value;
        setPageSelected(selectedValue)
        const newSize = selectedValue?.toLowerCase() === "all" ? data?.length + 1 : Number(selectedValue);

        // Calculate new page number to ensure correct positioning after page size change
        const newPage = Math.floor((currentPage - 1) * pageSize / newSize) + 1;

        // Update the page size
        setPageSize(newSize);

        setPage(newPage);

        // Update the current page to the recalculated value
        setCurrentPage(newPage);
    };

    // Handle comment edit on click
    const handleCommentClick = (index) => {
        setEditingRowIndex(index);
    };

    // Handle when a comment is being edited
    const handleCommentEdit = (index) => {
        setEditingRowIndex((prev) => [...prev, index]);
    };

    // Handle input change for the comment
    const handleCommentChange = (index, change, value) => {
        setEditedComments((prev) => ({
            ...prev,
            [index]: {
                comment: value,
                change
            }
        }));
    };

    // Handle comment blur and reset to original value if not saved
    const handleCommentBlur = (index, originalValue) => {

    };

    const sortedData = data?.sort((a, b) => {
        const sortKey = sortColumn;
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        const isDateColumn = sortKey === 'date'; // Replace 'date' with your actual date column key
        const isANumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
        const isBNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);

        // Handle empty or null strings to move them to top or bottom based on sortOrder
        const isAEmpty = aValue === null || aValue === '';
        const isBEmpty = bValue === null || bValue === '';

        if (isAEmpty && !isBEmpty) return sortOrder === 'asc' ? -1 : 1;
        if (isBEmpty && !isAEmpty) return sortOrder === 'asc' ? 1 : -1;

        if (isDateColumn) {
            // Parse and compare dates
            const aDate = Date.parse(aValue); // Parse the date string
            const bDate = Date.parse(bValue);

            return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
        } else if (isANumber && isBNumber) {
            // Numeric sorting
            return sortOrder === 'asc'
                ? parseFloat(aValue) - parseFloat(bValue)
                : parseFloat(bValue) - parseFloat(aValue);
        } else {
            // String sorting
            return sortOrder === 'asc'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        }
    });

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData?.slice(startIndex, endIndex);

    const handleColumnClick = (column: string) => {
        // Check if the column is non-sortable
        if (nonSortableColumns.has(column)) {
            // Do nothing if the column is not sortable
            return;
        }

        // Toggle sorting order if the column is already sorted
        if (column === sortColumn) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set the new column and default to ascending order
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const handlePageClick = (page: number) => {
        handlePageNumberChange && handlePageNumberChange(page);
        setShowDropdown(false)
    };

    const handlePreviousPage = () => {
        setShowDropdown(false)
        if (currentPage > 1)
            handlePageNumberChange && handlePageNumberChange(currentPage - 1);
    };

    const handleNextPage = () => {
        setShowDropdown(false)
        const totalPages = Math.ceil(data?.length / pageSize);
        if (currentPage < totalPages)
            handlePageNumberChange && handlePageNumberChange(currentPage + 1);
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(data?.length / pageSize);

        const generatePaginationNumbers = () => {
            const pages: (number | string)[] = [];
            const maxVisiblePages = 6; // Maximum visible pages excluding ellipses

            if (totalPages <= maxVisiblePages) {
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else if (currentPage <= 3) {
                pages.push(1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages);
            } else if (currentPage > 3 && currentPage < totalPages - 2) {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            } else {
                pages.push(1, 2, 3, '...', totalPages - 2, totalPages - 1, totalPages);
            }

            return pages;
        };

        const paginationNumbers = generatePaginationNumbers();

        return (
            <div className="flex flex-col md:flex-row items-center justify-between mt-4">
                {/* Left aligned section: Previous button */}
                <button
                    className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === 1
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Previous
                </button>

                {/* Center aligned Pagination */}
                <div className="flex items-center justify-center gap-1 flex-wrap relative" style={{ marginRight: "70px" }}>
                    {paginationNumbers.map((page, index) => (
                        <button
                            key={index}
                            type="button"
                            className={`inline-flex items-center justify-center min-w-[30px] h-[30px] px-1.5 flex-shrink-0 whitespace-nowrap text-neutral-25 ${
                                typeof page === 'number' && page >= 1000 ? 'text-xs' : 'text-sm'
                            } ${currentPage === page ? "rounded-full bg-darkcolor-500" : ""} ${
                                page === '...' ? 'cursor-default' : ''
                            }`}
                            onClick={() => typeof page === 'number' && handlePageClick(page)}
                            disabled={page === '...'}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ))}
                </div>


                {/* Right aligned Next button */}
                <button
                    className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] mt-2 md:mt-0 ${currentPage === totalPages
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
            </div>
        );
    };


    const renderSortIcon = (column: string) => {
        if (nonSortableColumns.has(column)) return null;
        const isActive = sortColumn === column;
        return (
            <FontAwesomeIcon
                icon={faSort}
                className={`table-sort-icon ml-1 ${isActive && sortOrder === "asc" ? "table-sort-icon--asc" : ""}`}
            />
        );
    };

    const getColumnColorOrHex = (column: string, item: { [key: string]: any }): string => {
        if (column === "alarms") {
            return item[column] === "OK" ? "green" : "red";
        } else if (column === "lastCom") {
            return item["hexcode"] || ""; // Return hexcode if it exists
        }
        return ""; // Default case
    };

    const showTooltip = (e, text) => {
        const tooltip = document.getElementById('custom-tooltip');
        if (!tooltip) return;

        tooltip.textContent = text;
        tooltip.classList.add('visible');

        const offsetX = 10;
        const offsetY = 30;
        const tooltipWidth = tooltip.offsetWidth;
        const screenWidth = window.innerWidth;

        // Flip tooltip if near right edge
        let left = e.clientX + offsetX;
        if (left + tooltipWidth > screenWidth) {
            left = screenWidth - tooltipWidth - 10;
        }

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${e.clientY - offsetY}px`;
    };

    const hideTooltip = () => {
        const tooltip = document.getElementById('custom-tooltip');
        if (tooltip) {
            tooltip.classList.remove('visible');
        }
    };



    return (
        sortedData.length > 0 ?
            <div className="mx-auto w-full">
                <div className="bg-darkcolor-900">
                    <div className="mt-8 flow-root">
                        <div className="overflow-x-auto border rounded-lg max-h-[580px]" style={{ borderColor: "rgb(74 84 99 / var(--tw-border-opacity))" }}>
                            <div className="inline-block min-w-full rounded-lg py-1 align-middle bg-darkcolor-700">
                                <table className="min-w-full divide-y divide-darkcolor-400 pl-4">
                                    <thead className="min-w-full divide-y divide-darkcolor-400">
                                        <tr>
                                            {columns.filter((column) => column !== "hexcode").map((column, index) => (
                                                <th
                                                    scope="col"
                                                    className={index === 0
                                                        ? "py-3 pl-5 px-3 text-left text-md font-semibold text-white"
                                                        : "px-3 py-3 text-left text-md font-semibold text-white"}
                                                    key={column}
                                                    onClick={() => handleColumnClick(column)}
                                                >
                                                    <div className="truncate max-w-[150px]">
                                                        {columnHeaders[index]}
                                                        {renderSortIcon(column)}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-darkcolor-400">
                                        {/* Render new rows at the top */}
                                        {paginatedData.filter((item) => item.added).map((item, index) => (
                                            <tr key={index}>
                                                {columns.map((column) => (
                                                    <td
                                                        key={column}
                                                        style={{
                                                            color: getColumnColorOrHex(column, item),
                                                            // whiteSpace: "pre-line",
                                                            wordBreak: "keep-all",
                                                            paddingLeft: column === "date" ? "20px" : "",
                                                            paddingRight: column === "comment" ? "30px" : "",
                                                            width:
                                                                column === "comment" ? "30%" : // Adjust comment width
                                                                    column === "change" ? "25%" : // or whatever width you prefer
                                                                        column === "date" ? "20%" : // Adjust date width
                                                                            column === "type" ? "15%" : // Adjust type width
                                                                                // Adjust change width
                                                                                "auto" // Default width for other columns
                                                        }}
                                                        className="px-3 py-3 text-sm text-gray-300"
                                                        onClick={() => column === "comment" && setEditingRowIndex([...editingRowIndex, item.eventId])}
                                                    >
                                                        {/* Render date conditionally */}
                                                        {column === "date" ? item.date : null}

                                                        {column === "type" ? item.type : null}

                                                        {column === "source" ? item.source : null}

                                                        {/* Directly render the change as an input field */}
                                                        {column === "change" ? item.change : null}

                                                        {/* Directly render the comment as an input field */}
                                                        {column === "comment" && (
                                                            <input
                                                                value={editedComments[item.eventId]?.comment ?? item.comment}
                                                                onChange={(e) => {
                                                                    const updatedRows = addedRows.map((row) =>
                                                                        row.eventId === item.eventId ? { ...row, comment: e.target.value } : row
                                                                    );
                                                                    const updateFilterRows = data?.map((row) =>
                                                                        row.eventId === item.eventId ? { ...row, comment: e.target.value } : row
                                                                    );
                                                                    setAddedRows(updatedRows);  // Update the change value in the state
                                                                    setFilterData(updateFilterRows);
                                                                    hideTooltip()
                                                                }}
                                                                className="bg-darkcolor-800 text-white px-2 py-1 rounded border"
                                                                style={{
                                                                    width: "100%", // Set width to 100% for input field
                                                                    boxSizing: "border-box",
                                                                    resize: "none",
                                                                    minHeight: "40px",
                                                                    lineHeight: "1.5",
                                                                    borderColor: "rgb(74 84 99 / var(--tw-border-opacity))"
                                                                }}
                                                            />
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}

                                        {/* Render existing data rows */}
                                        {paginatedData.filter((item) => !item.added).map((item, index) => (
                                            <tr key={index}>
                                                {columns
                                                    .map((column) => (
                                                        <td
                                                            key={column}
                                                            style={{
                                                                color: getColumnColorOrHex(column, item),
                                                                // whiteSpace: "pre-line",
                                                                wordBreak: "keep-all",
                                                                paddingLeft: column === "date" ? "20px" : "",
                                                                paddingRight: column === "comment" ? "30px" : "",
                                                                width: column === "comment" ? "30%" : // Adjust comment width
                                                                    column === "date" ? "20%" :
                                                                        column === "change" ? "25%" : // Adjust change width
                                                                            column === "type" ? "15%" : // Adjust type width
                                                                                "auto" // Default width for other columns
                                                            }}
                                                            className="px-4 py-4 text-sm text-gray-300"
                                                            onClick={() => column === "comment" && setEditingRowIndex([...editingRowIndex, item.eventId])}
                                                        >
                                                            {column === "comment" ? (
                                                                editingRowIndex.includes(item.eventId) ? (
                                                                    <input
                                                                        value={editedComments[item.eventId]?.comment ?? item[column]}
                                                                        onChange={(e) => {
                                                                            handleCommentChange(item.eventId,item.change, e.target.value)
                                                                            hideTooltip()
                                                                        }}
                                                                        onBlur={() => {
                                                                            handleCommentBlur(item.eventId, item[column])
                                                                            hideTooltip()
                                                                        }}
                                                                        className="bg-darkcolor-800 text-white px-2 py-1 rounded border"
                                                                        autoFocus
                                                                        // Removed native title
                                                                        style={{
                                                                            width: "100%",
                                                                            boxSizing: "border-box",
                                                                            resize: "none",
                                                                            minHeight: "40px",
                                                                            lineHeight: "1.5",
                                                                            borderColor: "rgb(74 84 99 / var(--tw-border-opacity))"
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div>
                                                                        <div
                                                                            className="text-cell"
                                                                            style={{
                                                                                width: "100%",
                                                                                maxWidth: "250px",
                                                                                display: "-webkit-box",
                                                                                WebkitBoxOrient: "vertical",
                                                                                WebkitLineClamp: 3,
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                                lineHeight: "1.4",
                                                                                whiteSpace: "normal",
                                                                                cursor: "default"
                                                                            }}

                                                                            onMouseOver={(e) => showTooltip(e, editedComments[item.eventId]?.comment ?? item[column])}
                                                                            onMouseLeave={hideTooltip}
                                                                        >
                                                                            {editedComments[item.eventId]?.comment ?? item[column]}
                                                                        </div>

                                                                        <span className="tooltip-text-custom">
                                                                            {editedComments[item.eventId]?.comment ?? item[column]}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            ) : (
                                                                item[column]
                                                            )}

                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div id="custom-tooltip" className="custom-tooltip" />
                </div>
                {pagniation && renderPagination()}
            </div>
            : <div className="mx-auto w-full">
                <div className="bg-darkcolor-900">
                    <div className="mt-8 flow-root">
                        <div className="overflow-x-auto border rounded-lg max-h-[580px]" style={{ borderColor: "rgb(74 84 99 / var(--tw-border-opacity))" }}>
                            <div className="inline-block min-w-full rounded-lg py-50 align-middle bg-darkcolor-700"><span style={{ marginLeft: "50%" }}>{showEventDataMessage ? " No Data" : "Loading..."}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
};
